'use client';

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

import { CategoryIcon } from '@/components/CategoryIcon';
import { buildCategoryIndexFromServerMeta } from '@/components/transactions/screen/categoryIndex';
import { TransactionDetailDrawer } from '@/components/transactions/screen/TransactionDetailDrawer';
import { adaptTransactionForScreen } from '@/components/transactions/screen/TransactionsScreen';
import {
  newSinceLastSyncIds,
  useHomeRecentTransactions,
} from '@/hooks/queries/useHomeRecentTransactions';
import { useFormatCurrency } from '@/hooks/useFormatCurrency';
import { SpaceTransactionMarker } from '@/components/spaces/SpaceSendAction';
import { useTransactionRename } from '@/hooks/useTransactionRename';
import {
  advanceHomeFeedWatermark,
  markHomeFeedSeenIds,
  readHomeFeedSeenSnapshot,
  type HomeFeedSeenState,
} from '@/lib/homeFeedSeen';
import { track, trackEvent } from '@/lib/telemetry';
import { categoryChipStyleFromColor, hashToCategoryColor } from '@/lib/transactions/categories';
import { localDayKey } from '@/lib/transactions/dates';
import { cn } from '@/lib/utils';
import type { Transaction } from '@/types';
import { WidgetCard, type WidgetState } from './WidgetCard';

const PAYMENT_LABELS = {
  pix: 'Pix',
  credit: 'Crédito',
  debit: 'Débito',
  transfer: 'TED / DOC',
  boleto: 'Boleto',
} as const;

// Same payment inference the transactions screen uses (`adaptTransactionForScreen`):
// raw Pluggy method when present, else the account type decides credit vs debit.
// An unrecognizable method simply drops off the subtitle instead of reading "Outros".
function paymentLabel(tx: Transaction): string | null {
  const raw = (tx.paymentMethod ?? (tx.account?.type === 'CREDIT' ? 'credit' : 'debit'))
    .toLowerCase();
  if (raw.includes('pix')) return PAYMENT_LABELS.pix;
  if (raw.includes('credit')) return PAYMENT_LABELS.credit;
  if (raw.includes('debit')) return PAYMENT_LABELS.debit;
  if (raw.includes('transfer') || raw === 'ted' || raw === 'doc') return PAYMENT_LABELS.transfer;
  if (raw.includes('boleto')) return PAYMENT_LABELS.boleto;
  return null;
}

// Grouping day follows the same precedence the transactions screen sorts by, read the same way
// the detail drawer renders it (local day of an instant, calendar day of a date-only value).
function periodDay(tx: Transaction): string {
  return localDayKey(tx.referenceDate ?? tx.effectiveDate ?? tx.date);
}

// "ter · 02 jul" (uppercased by CSS). Built from the date-only key as a LOCAL
// date so a UTC-midnight timestamp never shifts to the previous day in BRT.
function dayHeaderLabel(dayKey: string): string {
  const [year, month, day] = dayKey.split('-').map(Number);
  const date = new Date(year ?? 0, (month ?? 1) - 1, day ?? 1);
  // ptBR 'EEEEEE' is the 3-letter weekday ('qui'); 'EEE' would give 'quinta'.
  return `${format(date, 'EEEEEE', { locale: ptBR })} · ${format(date, 'dd MMM', { locale: ptBR })}`;
}

interface DayGroup {
  key: string;
  label: string;
  items: Transaction[];
}

// Group into a Map keyed by the day so same-day rows merge into ONE section even
// when they aren't adjacent in the `date`-sorted feed — credit-card items whose
// `referenceDate` (the grouping key) diverges from `date` otherwise split a day
// into two identical headers. Sections are ordered by day, newest first — the feed arrives
// sorted by the UTC instant, which can differ from the local-day order around midnight.
function groupTransactionsByDay(transactions: Transaction[]): DayGroup[] {
  const buckets = new Map<string, Transaction[]>();
  for (const tx of transactions) {
    const key = periodDay(tx);
    const bucket = buckets.get(key);
    if (bucket) bucket.push(tx);
    else buckets.set(key, [tx]);
  }
  return Array.from(buckets, ([key, items]) => ({ key, label: dayHeaderLabel(key), items }))
    .sort((a, b) => b.key.localeCompare(a.key));
}

function feedState(error: unknown, transactions: Transaction[] | undefined): WidgetState {
  if (error) return 'error';
  if (!transactions) return 'loading';
  if (transactions.length === 0) return 'empty';
  return 'ready';
}

function TransactionFeedRow({
  tx,
  isNew,
  onOpen,
}: {
  tx: Transaction;
  isNew: boolean;
  onOpen: () => void;
}): ReactNode {
  const formatCurrency = useFormatCurrency();
  const screenTx = adaptTransactionForScreen(tx);
  const displayMoney = screenTx.originalMoney ?? {
    amount: screenTx.amount,
    currencyCode: screenTx.currencyCode ?? 'BRL',
  };
  const amount = displayMoney.amount;
  const neutral = (tx.isCashFlowNeutral ?? tx.isInternalTransfer) === true;
  const chip = categoryChipStyleFromColor(
    hashToCategoryColor(tx.customCategoryId ?? tx.categoryId),
  );
  const category = tx.categoryTranslated?.trim() || 'Outros';
  const method = paymentLabel(tx);

  return (
    <li>
      <button
        type="button"
        onClick={onOpen}
        className="flex w-full items-center gap-3 py-2 text-left transition-opacity hover:opacity-80"
      >
        <span
          className="flex size-8 shrink-0 items-center justify-center rounded-xl"
          style={{ backgroundColor: chip.backgroundColor }}
          aria-hidden
        >
          <CategoryIcon
            name={tx.categoryIcon ?? tx.parentCategoryIcon}
            className="size-4"
            style={{ color: chip.color }}
          />
        </span>
        <div className="min-w-0 flex-1">
          <p className="flex items-center gap-1.5 truncate text-sm font-medium text-text-primary">
            <span className="truncate">
              {tx.customName ?? tx.merchantName ?? tx.aiRefinedDescription ?? tx.description}
            </span>
            {isNew && (
              <span className="shrink-0 rounded-full bg-info-surface px-1.5 py-0.5 text-3xs font-semibold text-info-text">
                Novo
              </span>
            )}
          </p>
          <p className="truncate text-xs text-text-secondary">
            {method ? `${category} · ${method}` : category}
          </p>
          <SpaceTransactionMarker transactionId={tx.id} />
        </div>
        <p
          className={cn(
            'shrink-0 font-mono text-sm tabular-nums',
            neutral
              ? 'text-text-tertiary'
              : amount >= 0
                ? 'text-amount-positive'
                : 'text-amount-negative',
          )}
        >
          {amount >= 0 ? '+' : ''}
          {formatCurrency(amount, { currency: displayMoney.currencyCode })}
        </p>
      </button>
    </li>
  );
}

export function UltimasWidget(): ReactNode {
  const { data, error } = useHomeRecentTransactions();
  const { userId } = useAuth();
  const groups = groupTransactionsByDay(data ?? []);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // Home dead-clicks land on descriptor text (VER-377) — the drawer opened from here
  // must offer the same click-to-rename as the Transactions page. The home feed key
  // sits under the 'transactions' prefix, so the hook's revalidation refreshes both
  // the rows and the drawer's selected transaction.
  const rename = useTransactionRename({ surface: 'home' });

  // "Novo" = arrived since the user LAST SAW this feed and not explicitly
  // handled (reviewed / opened) meanwhile. The seen state is snapshotted once
  // per page view (shared across the widget's mobile+desktop instances and
  // StrictMode re-runs — see readHomeFeedSeenSnapshot) — the flags hold
  // steady for the current view — and the watermark advances right after, so
  // the next visit comes clean. First visit ever (no watermark) falls back to
  // the last-sync-batch heuristic; rows synced in while the page stays open
  // land above the frozen snapshot and flag live.
  const [seen, setSeen] = useState<HomeFeedSeenState | null>(null);
  useEffect(() => {
    if (!userId || !data) return;
    // Read EAGERLY (not inside the state updater): the updater runs on the
    // NEXT render — after the advance effect below has already moved the
    // watermark — and would snapshot the spoiled value.
    const snapshot = readHomeFeedSeenSnapshot(userId);
    setSeen((prev) => prev ?? snapshot);
  }, [userId, data]);
  const newIds = useMemo(() => {
    if (!data || !seen) return new Set<string>();
    // Numeric comparison — the API serializes createdAt with a UTC offset that
    // is an environment detail; lexicographic ordering would break the day the
    // serialization shape changes (e.g. -03:00 vs Z).
    const watermarkMs = seen.watermark ? Date.parse(seen.watermark) : NaN;
    const candidates = Number.isFinite(watermarkMs)
      ? new Set(
          data
            .filter((tx) => tx.createdAt && Date.parse(tx.createdAt) > watermarkMs)
            .map((tx) => tx.id),
        )
      : newSinceLastSyncIds(data);
    return new Set([...candidates].filter((id) => !seen.seenIds.has(id)));
  }, [data, seen]);

  useEffect(() => {
    if (!userId || !data?.length) return;
    const newest = data
      .map((tx) => tx.createdAt)
      .filter((stamp): stamp is string => !!stamp)
      .sort()
      .at(-1);
    if (newest) advanceHomeFeedWatermark(userId, newest);
  }, [userId, data]);

  // Rows open the SAME detail drawer as the Transactions page: adapt the feed
  // to ScreenTransactions and derive the category index from them (the drawer
  // lazily fetches the Pix/boleto/receipt enrichment by id itself).
  const screenTransactions = useMemo(() => (data ?? []).map(adaptTransactionForScreen), [data]);
  const categoryIndex = useMemo(
    () => buildCategoryIndexFromServerMeta(undefined, screenTransactions),
    [screenTransactions],
  );
  const selected = selectedId
    ? screenTransactions.find((tx) => tx.id === selectedId) ?? null
    : null;

  const openDetails = (id: string): void => {
    setSelectedId(id);
    // Opening the detail is the strongest "seen" signal there is — persist it
    // AND reflect it in the current view (the chip drops right away).
    if (userId) markHomeFeedSeenIds(userId, [id]);
    setSeen((prev) =>
      prev ? { ...prev, seenIds: new Set([...prev.seenIds, id]) } : prev,
    );
    trackEvent('new_home.widget_action', { widget: 'ultimas', action: 'open_transaction' });
    track('action.transaction.open_detail', { source: 'home_ultimas' });
  };

  return (
    <WidgetCard
      eyebrow="Últimas transações"
      action={{ label: 'Ver todas', href: '/transactions' }}
      state={feedState(error, data)}
      emptyLabel="Sem transações recentes"
    >
      <div className="flex flex-col gap-2">
        {groups.map((group) => (
          <section key={group.key}>
            <h3 className="mb-0.5 font-mono text-2xs font-medium tracking-wider text-text-tertiary uppercase">
              {group.label}
            </h3>
            <ul className="divide-y divide-border-subtle">
              {group.items.map((tx) => (
                <TransactionFeedRow
                  key={tx.id}
                  tx={tx}
                  isNew={newIds.has(tx.id)}
                  onOpen={() => openDetails(tx.id)}
                />
              ))}
            </ul>
          </section>
        ))}
      </div>
      <TransactionDetailDrawer
        open={!!selected}
        onOpenChange={(open) => {
          if (!open) setSelectedId(null);
        }}
        tx={selected}
        categoryIndex={categoryIndex}
        onRename={rename.handleRename}
        onPreviewRenameBatch={rename.handlePreviewRenameBatch}
        isRenameSaving={rename.isRenameSaving}
      />
    </WidgetCard>
  );
}
